import React from 'react';

import { useIntl } from 'react-intl';

import { TLocalizationKey } from 'types/i18n';

import { EmptyDeliveryLogo } from './EmptyDeliveryLogo';
import { EmptyExternalLogo } from './EmptyExternalLogo';
import { EmptyFavoritesLogo } from './EmptyFavoritesLogo';
import { EmptyForYouLogo } from './EmptyForYouLogo';
import { EmptyOffersLogo } from './EmptyOffersLogo';
import {
  EmptyCTAContainer,
  EmptyCTAText,
  EmptyDescription,
  EmptyOffersContainer,
  EmptyTitle,
  ImageContainer,
} from './EmptyOffersView.styles';
import { EmptyPickupLogo } from './EmptyPickupLogo';
import { OfferSignInLogo } from './OffersSignInLogo';

type TFilterTabMap = {
  [key: number]: {
    empty?: {
      logo: (props: { height: string; width: string }) => JSX.Element;
      titleId: TLocalizationKey;
      descriptionId: TLocalizationKey;
      ctaId: TLocalizationKey;
    };
    loggedOut?: {
      logo: (props: { height: string; width: string }) => JSX.Element;
      titleId: TLocalizationKey;
      descriptionId: TLocalizationKey;
      ctaId: TLocalizationKey;
    };
  };
};

const filterTabToEntitiesMap: TFilterTabMap = {
  0: {
    empty: {
      logo: EmptyOffersLogo,
      titleId: 'offersNotAvailableTitle',
      descriptionId: 'offersNotAvailableDescription',
      ctaId: 'browseOurMenu',
    },
    loggedOut: {
      logo: EmptyOffersLogo,
      titleId: 'offersNotAvailableTitle',
      descriptionId: 'offersNotAvailableDescription',
      ctaId: 'browseOurMenu',
    },
  },
  1: {
    empty: {
      logo: EmptyForYouLogo,
      titleId: 'forYouOffersEmptyTitle',
      descriptionId: 'forYouOffersEmptyDescription',
      ctaId: 'seeAllOffers',
    },
    loggedOut: {
      logo: OfferSignInLogo,
      titleId: 'offersSignInTitle',
      descriptionId: 'offersSignInDescription',
      ctaId: 'offersSignInCta',
    },
  },
  2: {
    empty: {
      logo: EmptyFavoritesLogo,
      titleId: 'favoriteOffersEmptyTitle',
      descriptionId: 'favoriteOffersEmptyDescription',
      ctaId: 'seeAllOffers',
    },
    loggedOut: {
      logo: OfferSignInLogo,
      titleId: 'offersSignInTitle',
      descriptionId: 'offersSignInDescription',
      ctaId: 'offersSignInCta',
    },
  },
  3: {
    empty: {
      logo: EmptyDeliveryLogo,
      titleId: 'deliveryOffersEmptyTitle',
      descriptionId: 'deliveryOffersEmptyDescription',
      ctaId: 'seeAllOffers',
    },
    loggedOut: {
      logo: OfferSignInLogo,
      titleId: 'deliveryOffersEmptyTitle',
      descriptionId: 'deliveryOffersEmptyDescription',
      ctaId: 'seeAllOffers',
    },
  },
  4: {
    empty: {
      logo: EmptyPickupLogo,
      titleId: 'pickUpOffersEmptyTitle',
      descriptionId: 'pickUpOffersEmptyDescription',
      ctaId: 'seeAllOffers',
    },
    loggedOut: {
      logo: OfferSignInLogo,
      titleId: 'pickUpOffersEmptyTitle',
      descriptionId: 'pickUpOffersEmptyDescription',
      ctaId: 'seeAllOffers',
    },
  },
  5: {
    empty: {
      logo: EmptyExternalLogo,
      titleId: 'externalOffersEmptyTitle',
      descriptionId: 'externalOffersEmptyDescription',
      ctaId: 'seeAllOffers',
    },
    loggedOut: {
      logo: EmptyExternalLogo,
      titleId: 'externalOffersEmptyTitle',
      descriptionId: 'externalOffersEmptyDescription',
      ctaId: 'seeAllOffers',
    },
  },
};

export const EmptyOffersView = (props: {
  filterTab: number;
  isEmpty?: boolean;
  isLoggedOut?: boolean;
  ctaAction?: VoidFunction;
}) => {
  const { filterTab, isEmpty, isLoggedOut, ctaAction } = props;

  const { formatMessage } = useIntl();

  let LogoComponent: ((props: { height: string; width: string }) => JSX.Element) | undefined;
  let titleId: TLocalizationKey | '' = '';
  let descriptionId: TLocalizationKey | '' = '';
  let ctaTextId: TLocalizationKey | '' = '';

  if (isLoggedOut) {
    LogoComponent = filterTabToEntitiesMap[filterTab]?.loggedOut?.logo;
    titleId = filterTabToEntitiesMap[filterTab]?.loggedOut?.titleId || '';
    descriptionId = filterTabToEntitiesMap[filterTab]?.loggedOut?.descriptionId || '';
    ctaTextId = filterTabToEntitiesMap[filterTab]?.loggedOut?.ctaId || '';
  } else if (isEmpty) {
    LogoComponent = filterTabToEntitiesMap[filterTab]?.empty?.logo;
    titleId = filterTabToEntitiesMap[filterTab]?.empty?.titleId || '';
    descriptionId = filterTabToEntitiesMap[filterTab]?.empty?.descriptionId || '';
    ctaTextId = filterTabToEntitiesMap[filterTab]?.empty?.ctaId || '';
  }

  return (
    <EmptyOffersContainer>
      {LogoComponent && (
        <ImageContainer>
          <LogoComponent height="6.25rem" width="7.4375rem" />
        </ImageContainer>
      )}
      {titleId && <EmptyTitle>{formatMessage({ id: titleId })}</EmptyTitle>}
      {descriptionId && <EmptyDescription>{formatMessage({ id: descriptionId })}</EmptyDescription>}
      <EmptyCTAContainer onClick={() => ctaAction?.()}>
        {ctaTextId && <EmptyCTAText>{formatMessage({ id: ctaTextId })}</EmptyCTAText>}
      </EmptyCTAContainer>
    </EmptyOffersContainer>
  );
};
